import type { CSSProperties, ReactNode } from 'react';
import { useT } from '@/i18n/react';
import './optionSwitch.css';

/**
 * Csúszkás választó — a Grund képernyő réteg-váltójának formája, általánosan.
 *
 * Miért nem `SegmentedControl`? Mert ott minden elem külön gomb-doboz, és
 * kettőnél az „egyik VAGY másik" viszony nem látszik. Itt egyetlen pirulán
 * fut a csúszka: egy pillantásra kiderül, melyik oldalon állsz.
 *
 * ⚠️ KETTŐ VAGY HÁROM elemre való ALAPMÉRETBEN. Négytől a feliratok mobilon
 * már nem férnek ki 14 képpontos félkövérrel — ott vagy a `size="sm"` kell
 * (kisebb betű és szűkebb bélés; a Grund toplista időszak-választója így
 * fut négy állással), vagy a `SegmentedControl`.
 *
 * A `LayerSwitch` szándékosan külön maradt: az `role="switch"`, mert két
 * egymást kizáró RÉTEG között billen, nem egy listából választ.
 */

export interface OptionSwitchItem<T extends string> {
  value: T;
  label: string;
  /** Elhagyható vezérikon a felirat előtt. */
  icon?: ReactNode;
  /**
   * Kis lila pötty a felirat jobb felső sarkán — „van itt valami új" (#71,
   * Üzenetek: új kérelem, olvasatlan banda-üzenet). Képernyőolvasónak a
   * `dotLabel` (alapból „új") a felirat része lesz.
   */
  dot?: boolean;
  dotLabel?: string;
}

export interface OptionSwitchProps<T extends string> {
  options: readonly OptionSwitchItem<T>[];
  /** `null`: nincs kiválasztott elem — a csúszka ilyenkor nem látszik. */
  value: T | null;
  onChange: (value: T) => void;
  /** Kötelező: a csoport neve képernyőolvasónak. */
  label: string;
  /** `sm`: kisebb betű és szűkebb bélés — négy állás is kifér mobilon. */
  size?: 'sm' | 'md';
  disabled?: boolean;
}

export function OptionSwitch<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
  disabled = false,
}: OptionSwitchProps<T>) {
  const t = useT();
  const index = options.findIndex((option) => option.value === value);
  const active = index < 0 ? 0 : index;

  function move(delta: number) {
    const next = options[(active + delta + options.length) % options.length];
    if (next) onChange(next.value);
  }

  /*
    A kiválasztott érték a gyökér `data-value`-jában is ott van: erre
    horgonyozhat a hívó, ha a csúszka színét állásonként cserélné — lásd
    `--optsw-thumb` és a rögzítés képernyő mozgásforma-színeit.
  */
  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled}
      className={`optsw${size === 'sm' ? ' optsw--sm' : ''}${index < 0 ? ' optsw--unset' : ''}`}
      data-value={value ?? undefined}
      style={
        {
          '--optsw-count': options.length,
          '--optsw-active': active,
        } as CSSProperties
      }
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          move(1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          move(-1);
        }
      }}
    >
      {/* A csúszka a feliratok ALATT fut, ezért egyik állásban sem takar. */}
      <span className="optsw__thumb" aria-hidden="true" />
      {options.map((option, position) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            role="radio"
            aria-checked={checked}
            tabIndex={checked || (index < 0 && position === 0) ? 0 : -1}
            className={`optsw__option${checked ? ' optsw__option--on' : ''}`}
            onClick={() => onChange(option.value)}
          >
            {option.icon}
            {option.dot ? (
              <span className="optsw__label">
                {option.label}
                <span className="optsw__dot" aria-hidden="true" />
                <span className="optsw__sr">{`, ${option.dotLabel ?? t.common.newItem}`}</span>
              </span>
            ) : (
              option.label
            )}
          </button>
        );
      })}
    </div>
  );
}
